import { Box, IconButton } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import type { GridApiPro } from '@mui/x-data-grid-pro';
import type { GetArAgingReportResponse } from '@vy/api-definition';
import { OUTSTANDING_BREAKDOWN_OPTIONS } from '@vy/api-definition';
import { VyIcon } from '@vy/ui-design-system';
import type { ItemOf, ValueOf } from '@vy/utils';
import { isNullOrUndefined, sumBy, withDefault } from '@vy/utils';
import ExcelJs from 'exceljs';
import React, { useMemo } from 'react';
import { VyTableFilter } from '../../../../../components/new-design/vyTableFilter';
import { VyTableToolbar } from '../../../../../components/new-design/table/vy-table-toolbar/vy-table-toolbar';
import { useArAgingReport } from '../../../../../hooks/requests/use-ar-aging-report';
import { useSearchWithSearchParams } from '../../../../../hooks/use-search-with-search-params';
import { saveExcel } from '../../../../../utils/save-excel';
import { toastFromPromise } from '../../../../../utils/vy-toasts';
import { createInvoicesWorksheet } from '../../reports-page-utils/customer-revenue-report-utils/worksheet-creators/create-invoices-worksheet';
import { createSummaryWorksheet } from '../../reports-page-utils/customer-revenue-report-utils/worksheet-creators/create-summary-worksheet';
import {
  reportFilterButtonWrapperSx,
  reportIconButtonSx,
  reportSearchInputSx,
} from '../../../reports-components/report-toolbar-styles';
import { TOTALS_ROW_ID } from '../../../reports-components/report-table-consts';
import { ArAgingReportTable } from './ArAgingReportTable';
import { buildSummaryFromBreakdown } from './build-summary-from-breakdown';
import { useArAgingFilterFields } from './use-ar-aging-filter-fields';
import { applyArAgingFilters, useArAgingFilters } from './use-ar-aging-filters';

function sumBreakdownBucket(
  rows: GetArAgingReportResponse['summary'],
  key: ValueOf<typeof OUTSTANDING_BREAKDOWN_OPTIONS>,
) {
  return {
    grossAmount: sumBy(rows, (row) => row.outstandingBreakdown[key]?.grossAmount ?? 0),
    invoicesCount: sumBy(rows, (row) => row.outstandingBreakdown[key]?.invoicesCount ?? 0),
  };
}

function buildTotalsRow(
  rows: GetArAgingReportResponse['summary'],
): ItemOf<GetArAgingReportResponse['summary']> {
  return {
    customer: { id: TOTALS_ROW_ID, name: 'Total' },
    outstandingBreakdown: {
      [OUTSTANDING_BREAKDOWN_OPTIONS.UP_TO_ONE_MONTH]: sumBreakdownBucket(rows, OUTSTANDING_BREAKDOWN_OPTIONS.UP_TO_ONE_MONTH),
      [OUTSTANDING_BREAKDOWN_OPTIONS.ONE_TO_TWO_MONTHS]: sumBreakdownBucket(rows, OUTSTANDING_BREAKDOWN_OPTIONS.ONE_TO_TWO_MONTHS),
      [OUTSTANDING_BREAKDOWN_OPTIONS.TWO_TO_THREE_MONTHS]: sumBreakdownBucket(rows, OUTSTANDING_BREAKDOWN_OPTIONS.TWO_TO_THREE_MONTHS),
      [OUTSTANDING_BREAKDOWN_OPTIONS.MORE_THAN_THREE_MONTHS]: sumBreakdownBucket(rows, OUTSTANDING_BREAKDOWN_OPTIONS.MORE_THAN_THREE_MONTHS),
    },
  };
}

export const ArAgingReportSection: React.FC = () => {
  const { data: arAgingReport, isLoading } = useArAgingReport();
  const [searchText, setSearchText] = useSearchWithSearchParams();
  const apiRef = useGridApiRef();

  const allBreakdown = withDefault(arAgingReport?.summaryBreakdown, []);

  const { filters, handleFilterChange } = useArAgingFilters();
  const filterFields = useArAgingFilterFields(allBreakdown);

  const filteredBreakdown = useMemo(() => {
    let items = applyArAgingFilters(allBreakdown, filters);

    const query = searchText?.toLowerCase() ?? '';
    if (query) {
      items = items.filter((item) => item.customer.name.toLowerCase().includes(query));
    }

    return items;
  }, [allBreakdown, filters, searchText]);

  const filteredSummary = useMemo(() => {
    return buildSummaryFromBreakdown(filteredBreakdown);
  }, [filteredBreakdown]);

  const pinnedRows = useMemo(() => {
    return { bottom: [buildTotalsRow(filteredSummary)] };
  }, [filteredSummary]);

  const handleExportToCsv = async () => {
    if (isNullOrUndefined(arAgingReport)) return;

    const filteredReport: GetArAgingReportResponse = {
      ...arAgingReport,
      summary: filteredSummary,
      summaryBreakdown: filteredBreakdown,
    };

    await toastFromPromise(
      (async () => {
        const workbook = new ExcelJs.Workbook();
        createInvoicesWorksheet(workbook, filteredReport);
        createSummaryWorksheet(workbook, filteredReport);
        await saveExcel(workbook, 'AR Aging Report');
      })(),
      {
        pending: 'Downloading...',
        success: 'Download complete',
        error: 'Failed to download',
      },
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          px: 2,
          pt: 4,
          pb: 2,
          display: "flex",
          alignItems: "flex-end",
          flexWrap: "wrap",
          gap: 2
        }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: '1 1 200px',
            maxWidth: 300,
            minWidth: 160
          }}>
          <VyTableToolbar.SearchInput
            showLabel={false}
            onChange={(event) => setSearchText(event.target.value)}
            defaultValue=""
            placeholder="Search"
            sx={reportSearchInputSx}
          />
        </Box>

        <Box sx={reportFilterButtonWrapperSx}>
          <VyTableFilter
            fields={filterFields}
            defaultFilters={filters}
            onFiltersChange={handleFilterChange}
          />
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            minWidth: 16
          }} />

        <IconButton
          onClick={handleExportToCsv}
          data-vy="ar-aging-report-export"
          sx={reportIconButtonSx}
        >
          <VyIcon icon="Download" style={{ transform: 'scale(0.65)' }} />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <ArAgingReportTable
          apiRef={apiRef as React.MutableRefObject<GridApiPro>}
          items={filteredSummary}
          breakdownItems={filteredBreakdown}
          pinnedRows={pinnedRows}
          loadingItems={isLoading}
        />
      </Box>
    </Box>
  );
};
